import type { ReactNode } from 'react';
import React, { useCallback } from 'react';
import millify from 'millify';
import { Virtuoso } from 'react-virtuoso';
import type { FollowButtonSource } from '@soundxyz/utils/analytics';
import { StandardModal } from '@components/main-components/modals/StandardModal';
import { useMediaQuery } from '@context/mediaQuery';
import { useModal } from '@context/modal';
import type { FragmentType, UserModalRowFragmentDoc } from '@graphql/reactQuery';
import { CollectorModalInfoFragmentDoc, getFragment, gql } from '@graphql/reactQuery';
import { getSingularOrPlural } from '@utils/helpers';
import { UserRow, UserRowSkeleton } from './UserRow';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorModalInfo on ReleaseCollector {
    id
    nftsCount
    user {
      id
      ...UserModalRow
    }
    ownedGoldenEgg {
      id
    }
    vipNftsCount
  }
`);

const userItemContent = (
  _: number,
  user: FragmentType<UserModalRowFragmentDoc>,
  context: VirtuosoListContext,
) => <UserRow user={user} sourceLocation={context.sourceLocation} />;

const collectorItemContent = (
  _: number,
  collector: FragmentType<CollectorModalInfoFragmentDoc>,
  context: VirtuosoListContext,
) => {
  const { user, nftsCount, ownedGoldenEgg, vipNftsCount } = getFragment(
    CollectorModalInfoFragmentDoc,
    collector,
  );
  return (
    <UserRow
      user={user}
      secondaryText={`${millify(nftsCount)} ${getSingularOrPlural({
        count: nftsCount,
        word: 'edition',
      })}`}
      sourceLocation={context.sourceLocation}
      hasGoldenEgg={ownedGoldenEgg != null}
      isVip={vipNftsCount > 0}
    />
  );
};

type VirtuosoListContext = {
  sourceLocation: FollowButtonSource;
};

export const UserList = ({
  primaryCount,
  primaryItem,
  primarySubordinate,
  secondaryCount,
  secondaryItem,
  isLoading,
  loadMore,
  sourceLocation,
  ...props
}: {
  primaryCount: number | undefined;
  primaryItem: string;
  primarySubordinate?: string;
  secondaryCount?: number;
  secondaryItem?: string;
  sourceLocation: FollowButtonSource;
  isLoading: boolean;
  loadMore: () => void;
} & (
  | { userList: FragmentType<UserModalRowFragmentDoc>[] | undefined }
  | { collectorList: FragmentType<CollectorModalInfoFragmentDoc>[] | undefined }
)) => {
  const { tablet } = useMediaQuery();
  const { closeCancelModal } = useModal();
  const [scrollParent, setScrollParent] = React.useState<HTMLDivElement | undefined>();

  const LoadingFooter = useCallback(
    () =>
      isLoading ? (
        <>
          <UserRowSkeleton />
          <UserRowSkeleton />
          <UserRowSkeleton />
        </>
      ) : null,
    [isLoading],
  );

  let bodyContent: ReactNode;
  if ('collectorList' in props) {
    bodyContent = (
      <Virtuoso
        data={props.collectorList}
        itemContent={collectorItemContent}
        customScrollParent={scrollParent}
        endReached={loadMore}
        context={{ sourceLocation }}
        components={{ Footer: LoadingFooter }}
      />
    );
  } else if ('userList' in props) {
    bodyContent = (
      <Virtuoso
        data={props.userList}
        itemContent={userItemContent}
        customScrollParent={scrollParent}
        endReached={loadMore}
        context={{ sourceLocation }}
        components={{ Footer: LoadingFooter }}
      />
    );
  } else {
    return null;
  }

  const title = primaryCount
    ? `${millify(primaryCount)} ${getSingularOrPlural({
        word: primaryItem,
        count: primaryCount,
      })}${primarySubordinate ? ` ${primarySubordinate}` : ''}`
    : undefined;
  const subHeader =
    secondaryCount && secondaryItem
      ? `${millify(secondaryCount)} ${getSingularOrPlural({
          word: secondaryItem,
          count: secondaryCount,
        })}`
      : undefined;

  return (
    <StandardModal
      title={title}
      subHeader={subHeader}
      body={bodyContent}
      closeModal={closeCancelModal}
      ref={r => {
        setScrollParent(r || undefined);
      }}
      isFullScreen={!tablet}
    />
  );
};
